import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination } from 'swiper/modules';
import React, { useMemo } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, User } from 'lucide-react';
import moment from 'moment';
import { 
  useSessions, 
  useSpeakers 
} from '../../queries/leadLoginQueries';

const DashSession = () => {
  // Data fetching with error handling
  const { 
    data: sessionsResponse, 
    isLoading: sessionsLoading,
    isError: sessionsError,
    error: sessionsErrorData
  } = useSessions();

  const { 
    data: speakersResponse, 
    isLoading: speakersLoading,
    isError: speakersError,
    error: speakersErrorData
  } = useSpeakers();

  // Process data only when available
  const { venues, allSpeakers } = useMemo(() => {
    if (sessionsLoading || speakersLoading) {
      return { venues: [], allSpeakers: [] };
    }

    // Helper functions
    const convertTimeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr?.split(':')?.map(Number) || [0, 0];
      return (hours * 60) + minutes;
    };

    const sortSessionData = (sessions) => {
      if (!Array.isArray(sessions)) return [];
      
      return [...sessions].sort((a, b) => {
        const dateComparison = new Date(a.date) - new Date(b.date);
        return dateComparison === 0 
          ? convertTimeToMinutes(a.time_from) - convertTimeToMinutes(b.time_from)
          : dateComparison;
      });
    };

    const sortedSessions = sessionsResponse?.data 
      ? sortSessionData(sessionsResponse.data) 
      : [];

    // Group sessions by venue
    const groupedSessions = sortedSessions.reduce((acc, session) => {
      if (!session?.venue?.id) return acc;
      
      const venueId = session.venue.id;
      acc[venueId] = acc[venueId] || { 
        venue: session.venue, 
        sessions: [] 
      };
      acc[venueId].sessions.push(session);
      return acc;
    }, {});

    return {
      venues: Object.values(groupedSessions).sort((a, b) => 
        (a.venue.number || 0) - (b.venue.number || 0)
      ),
      allSpeakers: speakersResponse?.data || []
    };
  }, [sessionsResponse, speakersResponse, sessionsLoading, speakersLoading]);

  const formatSessionDateTime = (session) => {
    if (!session.date || !session.time_from || !session.time_to) return null;
    
    return {
      day: moment(session.date).format('MMM DD'),
      timeRange: `${
        moment(session.time_from, 'HH:mm:ss').format('h:mm A')
      }-${
        moment(session.time_to, 'HH:mm:ss').format('h:mm A')
      }`
    };
  };

  // Loading and error states
  if (sessionsLoading || speakersLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (sessionsError || speakersError) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">
          Error loading data: {
            sessionsErrorData?.message || 
            speakersErrorData?.message || 
            'Unknown error occurred'
          }
        </div>
      </div>
    );
  }

  return (
    <div className="bg-yellow-400 py-4">
      <div className="container mx-auto px-4">
        {/* <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Sessions Overview</h2>
          <div className="flex space-x-2">
            <button className="prev-page flex items-center bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition-colors duration-200">
              <ChevronLeft size={20} className="text-blue-600" />
            </button>
            <button className="next-page flex items-center bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition-colors duration-200">
              <ChevronRight size={20} className="text-blue-600" />
            </button>
          </div>
        </div> */}

        <div className="">
          <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={24}
            slidesPerView={3}
            navigation={{
              nextEl: '.next-page',
              prevEl: '.prev-page',
            }}
            pagination={{ clickable: true }}
            breakpoints={{
              320: { slidesPerView: 1 },
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
              1280: { slidesPerView: 3 }
            }}
          >
            {venues.map((venueGroup) => (
              <SwiperSlide key={venueGroup.venue.id}>
                <VenueCard 
                  venueGroup={venueGroup}
                  allSpeakers={allSpeakers}
                  formatSessionDateTime={formatSessionDateTime}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
};

// Extracted VenueCard component for better readability
const VenueCard = ({ venueGroup, allSpeakers, formatSessionDateTime }) => (
  <div className="h-full rounded-xl overflow-hidden flex flex-col">
    <div className="bg-white text-black p-4 mb-2 shadow-md rounded-lg">
      <div className="flex items-center">
        <div className="flex-shrink-0 flex items-center justify-center min-w-[1.5rem] h-[1.5rem] px-2 rounded-full bg-yellow-400 text-gray-900 font-bold mr-3">
          {venueGroup.venue.number}
        </div>
        <div className="flex-grow">
          <h3 className="font-semibold text-lg hover:text-yellow-500 transition-colors duration-200">
            {venueGroup.venue.name}
          </h3>
        </div>
      </div>
    </div>

    <div className="flex-grow overflow-y-auto p-1 max-h-96">
      {venueGroup.sessions.length > 0 ? (
        <div className="space-y-4">
          {venueGroup.sessions.map((session) => (
            <SessionCard 
              key={session.id}
              session={session}
              allSpeakers={allSpeakers}
              formatSessionDateTime={formatSessionDateTime}
              venueNumber={venueGroup.venue.number}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No sessions found for this venue
        </div>
      )}
    </div>
  </div>
);

// Extracted SessionCard component
const SessionCard = ({ session, allSpeakers, formatSessionDateTime, venueNumber }) => {
  const dateTime = formatSessionDateTime(session);
  
  return (
    <div className="rounded-lg bg-white px-4 py-4 shadow-md hover:shadow-xl transition-shadow duration-200">
      <div className="flex justify-between items-start mb-3">
        <div className="flex flex-wrap items-center gap-2">
          {dateTime && (
            <>
              <span className="inline-flex items-center py-0.5 rounded-full text-xs font-medium text-gray-600">
                <CalendarDays className="w-4 h-4 mr-1" />
                {dateTime.day}
              </span>
              <span className="inline-flex items-center text-xs text-gray-600">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {dateTime.timeRange}
              </span>
            </>
          )}
        </div>
        {/* {session.delegative_exclusive === "YES" && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-400 text-black">
            Delegate Only
          </span>
        )} */}
      </div>

      <div className="border-t border-gray-200 pt-3">
        <h3 className="text-lg font-bold text-black mb-2">{session.name}</h3>
        <span className="inline-block py-1 px-2 bg-gray-100 text-gray-800 text-xs font-medium rounded-full mb-3">
          {session.category?.name}
        </span>

        <PeopleLists 
          session={session}
          allSpeakers={allSpeakers}
          venueNumber={venueNumber}
        />
      </div>
    </div>
  );
};

// Extracted PeopleLists component
const PeopleLists = ({ session, allSpeakers, venueNumber }) => (
  <div className="space-y-3">
    {/* Speakers */}
    {session.session_speackers?.length > 0 && (
      <PeopleGroup 
        title="Speakers"
        people={session.session_speackers
          .sort((a, b) => a.id - b.id)
          .map(el => allSpeakers.find(s => s.id === el.speaker.id))
        }
      />
    )}

    {/* Moderators */}
    {session.session_moderators?.length > 0 && (
      <PeopleGroup 
        title="Chair"
        people={session.session_moderators
          .map(el => allSpeakers.find(s => s.id === el.speaker.id))
        }
      />
    )}

    {/* Participants */}
    {session.speakers?.length > 0 && (
      <PeopleGroup 
        title="Participants"
        people={session.speakers}
      />
    )}

    {/* Footer */}
    <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between items-center">
      {session.session_sponsors?.length > 0 && (
        <div className="text-xs text-gray-600">
          <span className="font-medium">Sponsor: </span>
          {session.session_sponsors.map((sponsor) => (
            <span key={sponsor.id} className="font-semibold">
              {sponsor.logo?.name}
            </span>
          ))}
        </div>
      )}
      <div className="flex-shrink-0 flex items-center justify-center min-w-[1.5rem] h-[1.5rem] px-2 rounded-full bg-yellow-400 text-gray-900 font-semibold">
        {venueNumber}
      </div>
    </div>
  </div>
);

// Reusable PeopleGroup component
const PeopleGroup = ({ title, people }) => (
  <div>
    <h4 className="text-sm font-semibold text-black mb-1">{title}</h4>
    <div className="flex flex-wrap gap-1">
      {people.filter(Boolean).map((person) => (
        <span key={person.id} className="inline-flex items-center px-2 py-1 rounded-full text-xs text-black bg-yellow-100 hover:bg-yellow-200 transition-colors duration-200">
          <User className="w-3 h-3 mr-1" />
          {person.heading}
        </span>
      ))}
    </div>
  </div>
);

export default DashSession;